// src/services/authService.js
import { auth, db } from "../firebase-config";
import { 
  signInWithEmailAndPassword, 
  signOut, 
  createUserWithEmailAndPassword, 
  updateProfile, 
  sendPasswordResetEmail,
  updatePassword 
} from "firebase/auth";
import { doc, setDoc, getDoc } from "firebase/firestore";

/**
 * Decodes the JWT token payload client-side to extract custom role claims.
 */
export const getUserRole = (token) => {
  if (!token) return null;
  try {
    const base64Url = token.split(".")[1];
    const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
    const jsonPayload = decodeURIComponent(
      window.atob(base64)
        .split("")
        .map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
        .join("")
    );
    const decoded = JSON.parse(jsonPayload);
    return decoded.role || null;
  } catch (error) {
    console.error("Error decoding token claims:", error);
    return null;
  }
};

/**
 * Evaluates the authenticated user's role and active status from Custom Claims and Firestore.
 */
export const getUserRoleAndStatus = async (user) => {
  if (!user) return { role: null, isDeactivated: false };

  if (user.disabled) {
    return { role: null, isDeactivated: true };
  }

  let role = null;
  let isDeactivated = false;

  // 1. Check custom claims
  try {
    const idTokenResult = await user.getIdTokenResult(true);
    if (idTokenResult.claims && idTokenResult.claims.role) {
      role = idTokenResult.claims.role;
    }
  } catch (e) {
    console.error("Error fetching user claims:", e);
  }

  const uid = user.uid;

  // 2. Check Instructor collection
  try {
    const instDocRef = doc(db, "instructors", uid);
    const instSnap = await getDoc(instDocRef);
    if (instSnap.exists()) {
      const data = instSnap.data();
      if (!role) role = "instructor";
      if (data.status === "deactivated") {
        isDeactivated = true;
      }
      return { role, isDeactivated };
    }
  } catch (e) {
    console.error("Error checking instructor collection:", e);
  }

  // 3. Check Student Profiles collection
  try {
    const studentDocRef = doc(db, "studentProfiles", uid);
    const studentSnap = await getDoc(studentDocRef);
    if (studentSnap.exists()) {
      const data = studentSnap.data();
      if (!role) role = "student";
      if (data.status === "deactivated") {
        isDeactivated = true;
      }
      return { role, isDeactivated };
    }
  } catch (e) {
    console.error("Error checking student profiles collection:", e);
  }

  // 4. Check Admin role by email
  const email = (user.email || "").toLowerCase();
  const isAdminEmail = email === "brainovenadmin@gmail.com" ||
                       email === "bo.admin@brainoven.in" ||
                       email === "admin@brainoven.com" ||
                       email.includes("admin");

  if (role === "admin" || isAdminEmail) {
    return { role: "admin", isDeactivated: false };
  }

  return { role, isDeactivated };
};

/**
 * Enforces role-based portal access immediately post-authentication.
 * Signs out user and clears storage if role is unauthorized or account is deactivated.
 */
export const verifyPortalAccess = async (user, expectedRole) => {
  if (!user) {
    throw new Error("Authentication required.");
  }

  const { role, isDeactivated } = await getUserRoleAndStatus(user);

  if (isDeactivated) {
    await signOut(auth);
    localStorage.clear();
    sessionStorage.clear();
    throw new Error("Account is disabled or deactivated.");
  }

  if (role !== expectedRole) {
    await signOut(auth);
    localStorage.clear();
    sessionStorage.clear();

    if (expectedRole === "admin") {
      throw new Error("Invalid Admin Credentials");
    } else if (expectedRole === "instructor") {
      throw new Error("Invalid Instructor Credentials");
    } else if (expectedRole === "student") {
      throw new Error("Invalid Student Credentials");
    } else {
      throw new Error("Unauthorized access.");
    }
  }

  return { role, isDeactivated };
};

// ==================== ADMIN AUTH ====================
export const login = async (email, password) => {
  try {
    const userCredential = await signInWithEmailAndPassword(auth, email, password);
    const user = userCredential.user;

    // Verify role before issuing session
    await verifyPortalAccess(user, "admin");

    const token = await user.getIdToken();
    localStorage.setItem("brainoven_token", token);
    return { token, user };
  } catch (error) {
    console.error("Firebase Admin Login Error:", error);
    throw new Error(error.message || "Invalid Admin Credentials");
  }
};

export const logout = async () => {
  try {
    await signOut(auth);
    localStorage.removeItem("brainoven_token");
    sessionStorage.clear();
  } catch (error) {
    console.error("Firebase Logout Error:", error);
    throw error;
  }
};

// ==================== INSTRUCTOR AUTH ====================
export const instructorLogin = async (email, password) => {
  try {
    const userCredential = await signInWithEmailAndPassword(auth, email, password);
    const user = userCredential.user;

    // Verify role before issuing session
    await verifyPortalAccess(user, "instructor");

    const token = await user.getIdToken();
    localStorage.setItem("brainoven_instructor_token", token);
    return { token, user };
  } catch (error) {
    console.error("Instructor Login Error:", error);
    throw new Error(error.message || "Invalid Instructor Credentials");
  }
};

export const instructorLogout = async () => {
  try {
    await signOut(auth);
    localStorage.removeItem("brainoven_instructor_token");
    sessionStorage.clear();
  } catch (error) {
    console.error("Instructor Logout Error:", error);
    throw error;
  }
};

export const changeInstructorPassword = async (newPassword) => {
  try {
    const user = auth.currentUser;
    if (!user) throw new Error("No authenticated user found.");
    await updatePassword(user, newPassword);
  } catch (error) {
    console.error("Change Instructor Password Error:", error);
    throw error;
  }
};

// ==================== STUDENT AUTH ====================
export const studentRegister = async (email, password, displayName, phone) => {
  let user = null;
  try {
    const userCredential = await createUserWithEmailAndPassword(auth, email, password);
    user = userCredential.user;
    
    // Update user display name in Firebase Auth
    await updateProfile(user, { displayName });
    
    // Save additional profile details in Firestore
    const profileRef = doc(db, "studentProfiles", user.uid);
    await setDoc(profileRef, {
      uid: user.uid,
      displayName,
      email: email.toLowerCase().trim(),
      phone,
      regNo: "BO-" + Math.floor(100000 + Math.random() * 900000),
      coins: 0,
      streakCurrent: 0,
      streakMax: 0,
      streakFreezes: 0,
      consecutiveLoginCount: 0,
      lastLoginDate: null,
      batchIds: [],
      createdAt: new Date().toISOString()
    });
    
    const token = await user.getIdToken();
    localStorage.setItem("brainoven_student_token", token);
    return { user, token };
  } catch (error) {
    console.error("Student Registration Error:", error);
    if (user) {
      try {
        await user.delete();
      } catch (cleanupError) {
        console.error("Could not roll back Auth user creation:", cleanupError.message);
      }
    }
    throw new Error(error.message || "Registration failed");
  }
};

export const studentLogin = async (email, password) => {
  try {
    const userCredential = await signInWithEmailAndPassword(auth, email, password);
    const user = userCredential.user;

    // Verify role before issuing session
    await verifyPortalAccess(user, "student");

    const token = await user.getIdToken();
    localStorage.setItem("brainoven_student_token", token);
    return { user, token };
  } catch (error) {
    console.error("Student Login Error:", error);
    throw new Error(error.message || "Invalid Student Credentials");
  }
};

export const studentLogout = async () => {
  try {
    await signOut(auth);
    localStorage.removeItem("brainoven_student_token");
    sessionStorage.clear();
  } catch (error) {
    console.error("Student Logout Error:", error);
    throw error;
  }
};

export const sendStudentPasswordReset = async (email) => {
  try {
    const cleanEmail = (email || "").toLowerCase().trim();
    const actionCodeSettings = {
      url: window.location.origin + "/student/login",
      handleCodeInApp: false
    };
    await sendPasswordResetEmail(auth, cleanEmail, actionCodeSettings);
  } catch (error) {
    console.error("Send Password Reset Error:", error);
    throw error;
  }
};

export const sendInstructorPasswordReset = async (email) => {
  try {
    const cleanEmail = (email || "").toLowerCase().trim();
    const actionCodeSettings = {
      url: window.location.origin + "/instructor/login",
      handleCodeInApp: false
    };
    await sendPasswordResetEmail(auth, cleanEmail, actionCodeSettings);
  } catch (error) {
    console.error("Send Instructor Password Reset Error:", error);
    throw error;
  }
};
